import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection,
  WSTableItem,
  WSTableToolbar
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import { PayableRow } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayableRows } from "@query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route, useHistory, useLocation } from "react-router-dom";
import { PayableDetails } from "../../../../Invoices/screens/payables/PayableDetails";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../../../query/payments/queries";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import {
  PARAMETER_PAYABLE_ID,
  PayablesTabSlug,
  routeHelpersPayablesDetails
} from "../../paths";
import { useBulkApprovePayables } from "../../utils/useBulkApprovePayables";
import { useBulkCancelPayables } from "../../utils/useBulkCancelPayables";
import { useBulkDeletePayables } from "../../utils/useBulkDeletePayables";
import { useBulkOpenPayables } from "../../utils/useBulkOpenPayables";
import { useBulkPreApprovePayables } from "../../utils/useBulkPreApprovePayables";
import { useBulkUnapprovePayables } from "../../utils/useBulkUnapprovePayables";
import { useDownloadPayablesCsv } from "../../utils/useDownloadPayablesCsv";
import { ColumnKey } from "./columns";
import { createSearchRequest } from "./filters/request";
import { isActiveStatusFilter } from "./filters/statusOptions";
import {
  Filter,
  PayablesTabFilter,
  PayablesVisibleFilters,
  SortField
} from "./filters/types";
import { useBuildConfig } from "./filters/config";
import { TablePayables } from "./TablePayables";

export type PayableBulkActionKey =
  | "approve"
  | "preapprove"
  | "unapprove"
  | "delete"
  | "open"
  | "cancel";

export type EmptyStateCopy = {
  firstTime?: string;
  standart: string;
};

type Props = {
  tab: PayablesTabFilter;
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  visibleFilters?: PayablesVisibleFilters;
  showDownloadCsv?: boolean;
  emptyState?: EmptyStateCopy;
  bulkActions?: PayableBulkActionKey[];
  // Restricts the list to a single payroll run.
  payrollRunId?: string;
  // When rendered outside the standard tab routes (e.g. the payroll run
  // detail page), this base path is used for the payable detail sidebar
  // route and row navigation instead of the tab-based detail route.
  detailsBasePath?: string;
};

const BULK_ACTION_LABELS: Record<PayableBulkActionKey, string> = {
  approve: "Approve for payment",
  preapprove: "Pre-approve",
  unapprove: "Unapprove",
  delete: "Delete",
  open: "Move to open",
  cancel: "Cancel"
};

export const FilterableTablePayables: React.FC<Props> = props => {
  const userId = useUserId();
  const { urlQuery, setUrlQuery } = useUrlQuery<Filter, SortField>();

  const payrollSettingsQuery = useQueryPayrollSettings(userId, {
    retry: false
  });
  const isDualStage =
    payrollSettingsQuery.data?.workflow === PayrollWorkflowStrategy.DualStage;

  const history = useHistory();
  const location = useLocation();

  const handleRowClick = useCallback(
    (payable: PayableRow) => {
      if (!payable.id) {
        return;
      }

      const pathname = props.detailsBasePath
        ? `${props.detailsBasePath}/${payable.id}`
        : routeHelpersPayablesDetails.createLocationObject({
            tab: props.tab as PayablesTabSlug,
            payableId: payable.id
          }).pathname;

      // Carry the current query string (active filters) into the detail URL
      // so the table keeps them underneath and they survive closing the
      // detail sidebar.
      history.push({
        pathname,
        search: location.search,
        state: { backPath: location.pathname }
      });
    },
    [
      history,
      location.pathname,
      location.search,
      props.detailsBasePath,
      props.tab
    ]
  );

  const [selection, setSelection] = useState<WSTableItem<PayableRow>[]>([]);
  const clearSelection = useCallback(() => setSelection([]), []);

  // Clear selection whenever the tab or filters/sort change to avoid acting
  // on stale rows that may no longer be visible.
  useEffect(() => {
    setSelection([]);
  }, [props.tab, urlQuery.filter, urlQuery.sort]);

  const bulkApprove = useBulkApprovePayables();
  const bulkPreApprove = useBulkPreApprovePayables();
  const bulkUnapprove = useBulkUnapprovePayables();
  const bulkDelete = useBulkDeletePayables();
  const bulkOpen = useBulkOpenPayables();
  const bulkCancel = useBulkCancelPayables();

  const bulkActionHandlers = useMemo<
    Record<PayableBulkActionKey, (payables: PayableRow[]) => void>
  >(
    () => ({
      approve: payables => bulkApprove(payables, clearSelection),
      preapprove: payables => bulkPreApprove(payables, clearSelection),
      unapprove: payables => bulkUnapprove(payables, clearSelection),
      delete: payables => bulkDelete(payables, clearSelection),
      open: payables => bulkOpen(payables, clearSelection),
      cancel: payables => bulkCancel(payables, clearSelection)
    }),
    [
      bulkApprove,
      bulkPreApprove,
      bulkUnapprove,
      bulkDelete,
      bulkOpen,
      bulkCancel,
      clearSelection
    ]
  );

  const bulkActionKeys = props.bulkActions;
  const hasBulkActions = !!bulkActionKeys && bulkActionKeys.length > 0;

  const request = useMemo(
    () =>
      createSearchRequest(urlQuery.filter, urlQuery.sort, props.tab, {
        isDualStage,
        payrollRunId: props.payrollRunId
      }),
    [urlQuery.filter, urlQuery.sort, props.tab, props.payrollRunId, isDualStage]
  );

  const filtersConfig = useBuildConfig(props.visibleFilters);
  const queryPayables =
    queriesPayableRows.searchPayableRows.usePaginatedQuery(request);
  const payables = queryPayables.flatData ?? [];
  const summary = queryPayables.data?.pages?.[0]?.summary;

  const downloadCsv = useDownloadPayablesCsv({ filter: request.filter });
  const canDownloadCsv = !!props.showDownloadCsv && !downloadCsv.isDisabled;

  const handleSortChange = useCallback(
    (field: SortField, direction: WSTableColumnSortDirection) => {
      setUrlQuery(prev => ({
        ...prev,
        sort: { field, direction }
      }));
    },
    [setUrlQuery]
  );

  const hasUserFilters = useMemo(() => {
    // The status filter's "All" option is a selection that narrows nothing, so
    // it must not flip the empty state to "No results found".
    const { status, ...rest } = urlQuery.filter ?? {};
    return (
      isActiveStatusFilter(status) ||
      Object.values(rest).some(value => value !== undefined)
    );
  }, [urlQuery.filter]);

  const renderEmptyState = (emptyState: EmptyStateCopy) => {
    const showFirstTime = !hasUserFilters && !!emptyState.firstTime;

    const description = showFirstTime
      ? emptyState.firstTime
      : emptyState.standart;

    return (
      <WSEmptyState
        py="3XL"
        hasBorder
        bgColor="gray"
        type={hasUserFilters ? "search" : "inbox"}
        title={hasUserFilters ? "No results found" : "No payables"}
        description={description}
      />
    );
  };

  const renderContent = () => {
    if (queryPayables.isLoading) {
      return <TablePayables loading payables={[]} />;
    }

    if (queryPayables.isError) {
      return (
        <WSEmptyState
          py="3XL"
          hasBorder
          bgColor="gray"
          type="error"
          title="Something went wrong"
          description="We couldn't load your payables."
          actions={{
            buttons: [
              { label: "Try again", onClick: () => queryPayables.refetch() }
            ]
          }}
        />
      );
    }

    if (payables.length === 0) {
      return props.emptyState ? renderEmptyState(props.emptyState) : null;
    }

    return (
      <WSInfiniteScroll
        mt="M"
        onLoad={() => queryPayables.fetchNextPage()}
        loadMore={payables.length > 0}
        endOfList={!queryPayables.hasNextPage}
        loading={!!queryPayables.isFetchingNextPage}
      >
        <TablePayables
          payables={payables}
          onRowClick={handleRowClick}
          onSortChange={handleSortChange}
          sortField={urlQuery.sort?.field}
          sortDirection={urlQuery.sort?.direction}
          visibleColumns={props.visibleColumns}
          canDownloadCsv={canDownloadCsv}
          isDownloadingCsv={downloadCsv.isLoading}
          onDownloadCsv={downloadCsv.download}
          selection={hasBulkActions ? selection : undefined}
          onSelectionChange={hasBulkActions ? setSelection : undefined}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <Route
        path={
          props.detailsBasePath
            ? `${props.detailsBasePath}/:${PARAMETER_PAYABLE_ID}`
            : routeHelpersPayablesDetails.path
        }
        component={PayableDetails}
        exact={!!props.detailsBasePath}
      />

      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={
          summary
            ? { count: summary.count, amount: summary.amountTotal }
            : undefined
        }
      />

      {bulkActionKeys && bulkActionKeys.length > 0 && selection.length > 0 && (
        <WSTableToolbar
          count={selection.length}
          onClear={clearSelection}
          actionsLabel="Bulk actions"
          actions={bulkActionKeys.map(key => ({
            label: BULK_ACTION_LABELS[key],
            destructive: key === "delete" || key === "cancel",
            onClick: () =>
              bulkActionHandlers[key](selection.map(item => item.data))
          }))}
        />
      )}

      {renderContent()}
    </WSList>
  );
};
